<template>
  <v-container style="max-width: 760px" class="py-4 py-sm-6 px-0 px-sm-3">

    <!-- Header -->
    <div class="d-flex flex-wrap align-center mb-4 mb-sm-8" style="gap: 4px">
      <v-btn icon class="mr-2" @click="$router.push({ name: 'empresa.productos.registrar.selector' })">
        <v-icon>mdi-chevron-left</v-icon>
      </v-btn>
      <v-chip :color="esElaboracionPropia ? 'green' : 'blue'" dark small class="mr-3">
        <v-icon left x-small>{{ esElaboracionPropia ? 'mdi-factory' : 'mdi-cart' }}</v-icon>
        {{ esElaboracionPropia ? 'Producción' : 'Catálogo' }}
      </v-chip>
      <span class="text-h6 font-weight-bold">Nuevo producto</span>
    </div>

    <!-- ─── STEP 1: Información ─── -->
    <div class="d-flex align-start mb-1">
      <div class="step-indicator">
        <div :class="['step-circle', step1Complete ? 'step-circle--done' : 'step-circle--active']">
          <v-icon v-if="step1Complete" dark small>mdi-check</v-icon>
          <span v-else>1</span>
        </div>
        <div class="step-connector"></div>
      </div>

      <v-card outlined class="flex-grow-1 ml-2 ml-sm-4 mb-3">
        <v-card-title class="subtitle-1 font-weight-bold pb-1">
          Información del producto
        </v-card-title>
        <v-card-text>
          <form-producto-descripcion
            :descripcion="producto.descripcion"
            @update:descripcion="producto.descripcion = $event"
          />

          <producto-marca-form
            :producto="producto"
            :outlined="true"
            @set_marca="producto.marca = $event"
          />

          <form-producto-barcode :producto="producto" />

          <v-autocomplete
            v-model="producto.proveedor"
            :items="proveedoresLocal"
            item-text="nombre"
            :return-object="true"
            label="Proveedor sugerido (opcional)"
            clearable
            outlined
            prepend-inner-icon="mdi-truck"
            :loading="loadingProveedores"
            no-data-text="Sin proveedores"
            @update:search-input="buscarProveedores"
          />

          <alicuota-iva-chips v-model="producto.alicuota_iva" />
        </v-card-text>
      </v-card>
    </div>

    <!-- ─── STEP 2: Clasificación ─── -->
    <div class="d-flex align-start mb-1" :class="{ 'step-disabled': !step1Complete }">
      <div class="step-indicator">
        <div :class="['step-circle', step2Complete ? 'step-circle--done' : step1Complete ? 'step-circle--active' : 'step-circle--pending']">
          <v-icon v-if="step2Complete" dark small>mdi-check</v-icon>
          <span v-else>2</span>
        </div>
        <div class="step-connector"></div>
      </div>

      <v-card outlined class="flex-grow-1 ml-2 ml-sm-4 mb-3" ref="step2Card">
        <v-card-title class="subtitle-1 font-weight-bold pb-1">
          Clasificación
        </v-card-title>
        <v-card-text>
          <producto-categoria-form
            :categoria="producto.categoria"
            label="Categoría"
            @set_categoria="setCategoria"
          />
        </v-card-text>
      </v-card>
    </div>

    <!-- ─── STEP 3: Presentaciones ─── -->
    <div class="d-flex align-start mb-1" :class="{ 'step-disabled': !step2Complete }">
      <div class="step-indicator">
        <div :class="['step-circle', step3Complete ? 'step-circle--done' : step2Complete ? 'step-circle--active' : 'step-circle--pending']">
          <v-icon v-if="step3Complete" dark small>mdi-check</v-icon>
          <span v-else>3</span>
        </div>
        <div class="step-connector"></div>
      </div>

      <v-card outlined class="flex-grow-1 ml-2 ml-sm-4 mb-3" ref="step3Card">
        <v-card-title class="subtitle-1 font-weight-bold pb-1 d-flex align-center">
          Presentaciones
          <v-tooltip bottom max-width="300" nudge-right="10">
            <template #activator="{ on }">
              <v-icon small class="ml-2 grey--text" v-on="on">mdi-help-circle-outline</v-icon>
            </template>
            <span>
              Una presentación es la forma en que vendés el producto.
              Por ejemplo: "Unidad", "Caja × 12", "Pack × 6".
              La presentación base se crea automáticamente y es la referencia de precio.
              Las demás derivan su costo multiplicando por el factor indicado.
            </span>
          </v-tooltip>
        </v-card-title>

        <v-card-text>
          <!-- Lista de presentaciones -->
          <v-list dense class="pa-0">
            <v-list-item
              v-for="(pp, i) in presentaciones"
              :key="i"
              class="px-0"
            >
              <v-list-item-icon class="mr-3 my-auto">
                <v-chip
                  x-small
                  :color="pp.es_base ? 'blue darken-1' : 'grey darken-1'"
                  dark
                  class="font-weight-bold"
                >
                  {{ pp.es_base ? 'BASE' : `×${pp.factor_conversion}` }}
                </v-chip>
              </v-list-item-icon>
              <v-list-item-content>
                <v-list-item-title class="body-2">{{ pp.nombre }}</v-list-item-title>
              </v-list-item-content>
              <v-list-item-action v-if="!pp.es_base">
                <v-btn icon x-small @click="eliminarPresentacion(i)">
                  <v-icon small color="grey">mdi-close</v-icon>
                </v-btn>
              </v-list-item-action>
            </v-list-item>
          </v-list>

          <!-- Formulario nueva presentación -->
          <v-expand-transition>
            <div v-if="mostrarFormPresentacion" class="mt-3">
              <v-divider class="mb-4" />
              <v-row dense>
                <v-col cols="12" sm="7">
                  <v-text-field
                    v-model="nuevaPresentacion.nombre"
                    label="Nombre (ej: Caja × 12)"
                    outlined
                    dense
                    autofocus
                  />
                </v-col>
                <v-col cols="12" sm="5">
                  <v-text-field
                    v-model.number="nuevaPresentacion.factor_conversion"
                    label="Cantidad de unidades"
                    outlined
                    dense
                    type="number"
                    min="2"
                    hint="¿Cuántas unidades contiene?"
                    persistent-hint
                  />
                </v-col>
              </v-row>
              <div class="d-flex mt-1">
                <v-btn
                  small
                  color="primary"
                  depressed
                  :disabled="!nuevaPresentacionValida"
                  @click="agregarPresentacion"
                >
                  Agregar
                </v-btn>
                <v-btn small text class="ml-2" @click="cancelarNuevaPresentacion">
                  Cancelar
                </v-btn>
              </div>
            </div>
          </v-expand-transition>

          <v-btn
            v-if="!mostrarFormPresentacion"
            small
            text
            color="primary"
            class="mt-3 px-0"
            @click="mostrarFormPresentacion = true"
          >
            <v-icon left small>mdi-plus</v-icon>
            Agregar otra presentación
          </v-btn>
        </v-card-text>
      </v-card>
    </div>

    <!-- ─── STEP 4: Precios & Sucursales ─── -->
    <div class="d-flex align-start mb-1" :class="{ 'step-disabled': !step3Complete }">
      <div class="step-indicator">
        <div :class="['step-circle', step3Complete ? 'step-circle--active' : 'step-circle--pending']">
          <span>4</span>
        </div>
        <!-- sin conector: último step -->
      </div>

      <v-card outlined class="flex-grow-1 ml-2 ml-sm-4 mb-3" ref="step4Card">
        <v-card-title class="subtitle-1 font-weight-bold pb-1 d-flex align-center">
          Precios y sucursales
          <v-chip x-small class="ml-2" color="grey lighten-2" text-color="grey darken-2">
            Opcional
          </v-chip>
        </v-card-title>
        <v-card-subtitle class="pt-0 pb-2 caption grey--text">
          Podés completarlo ahora o configurarlo después desde el producto.
        </v-card-subtitle>

        <v-card-text>
          <!-- Precio de costo base (presentación unitaria) -->
          <v-text-field
            v-model.number="precios.precio_costo_base"
            label="Precio de costo (presentación base)"
            outlined
            type="number"
            min="0"
            prefix="$"
          />

          <!-- Calculadora margen → precio venta -->
          <PrecioVentaCalculatorWrapper
            :precioCosto="precios.precio_costo_base != null ? String(precios.precio_costo_base) : '0'"
            :ganancia="precios.margen_ganancia_sugerido != null ? String(precios.margen_ganancia_sugerido) : '0'"
            :precioVenta="precios.precio_venta_sugerido != null ? String(precios.precio_venta_sugerido) : null"
            precioBase="0"
            :esPrecioVentaManual="true"
            :outlined="true"
            :showImpuestosInfo="false"
            :showPrecioSugerencia="false"
            ref="precioWrapper"
            @update:precioVenta="precios.precio_venta_sugerido = parseFloat($event) || null"
            @update:ganancia="precios.margen_ganancia_sugerido = parseFloat($event) || null"
          />

          <!-- Sucursales -->
          <div class="mt-2">
            <div class="subtitle-2 mb-2">¿En qué sucursales se va a vender?</div>
            <v-autocomplete
              v-model="sucursalesSeleccionadas"
              :items="sucursales"
              item-text="nombre"
              :return-object="true"
              multiple
              chips
              small-chips
              deletable-chips
              outlined
              label="Sucursales"
              prepend-inner-icon="mdi-store-outline"
              no-data-text="Sin sucursales"
              :loading="loadingSucursales"
            >
              <template #selection="{ item, selected }">
                <v-chip
                  small
                  :input-value="selected"
                  close
                  @click:close="quitarSucursal(item)"
                >
                  {{ item.nombre }}
                </v-chip>
              </template>
            </v-autocomplete>
          </div>

          <!-- Advertencia: sucursales sin costo -->
          <v-alert
            v-if="sucursalesSeleccionadas.length > 0 && !precios.precio_costo_base"
            dense
            text
            type="warning"
            class="caption mt-2"
          >
            Para configurar el precio en las sucursales seleccionadas es necesario ingresar el precio de costo.
          </v-alert>

          <!-- Info si hay presentaciones derivadas -->
          <v-alert
            v-if="presentaciones.length > 1 && precios.precio_costo_base"
            dense
            text
            type="info"
            class="caption mt-2"
          >
            El costo de las presentaciones derivadas se calculará automáticamente multiplicando
            el costo base por su factor de conversión.
          </v-alert>
        </v-card-text>
      </v-card>
    </div>

    <!-- ─── Footer ─── -->
    <div class="d-flex justify-end align-center mt-4 mb-8">
      <v-btn
        color="primary"
        large
        depressed
        :disabled="!step3Complete || loading"
        :loading="loading"
        @click="registrar"
      >
        Registrar producto
      </v-btn>
    </div>

  </v-container>
</template>

<script>
import { mapState, mapGetters, mapActions } from 'vuex';
import FormProductoDescripcion from '@/components/producto/form/form.producto.descripcion.vue';
import FormProductoBarcode from '@/components/producto/form/form.producto.barcode.vue';
import ProductoCategoriaForm from '@/components/producto/form/form.producto.categoria.vue';
import ProductoMarcaForm from '@/components/producto/form/form.producto.marca.vue';
import AlicuotaIvaChips from '@/components/producto/form/AlicuotaIvaChips.vue';
import PrecioVentaCalculatorWrapper from '@/components/producto/form/PrecioVentaCalculatorWrapper.vue';
import ProductosAPI from '@/api/productos.js';

export default {
  name: 'ProductoEmpresaRegistrarWizard',

  components: {
    AlicuotaIvaChips,
    FormProductoDescripcion,
    FormProductoBarcode,
    ProductoCategoriaForm,
    ProductoMarcaForm,
    PrecioVentaCalculatorWrapper,
  },

  $_veeValidate: {
    validator: 'new',
  },

  props: {
    esElaboracionPropia: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      loading: false,
      loadingProveedores: false,
      loadingSucursales: false,
      proveedoresLocal: [],
      debounceProveedores: null,

      producto: {
        descripcion: '',
        codigoDeBarra: null,
        marca: { id: null },
        proveedor: null,
        categoria: { id: null },
        alicuota_iva: null,
      },

      presentaciones: [
        { nombre: 'Unidad', factor_conversion: 1, es_base: true },
      ],
      nuevaPresentacion: { nombre: '', factor_conversion: null },
      mostrarFormPresentacion: false,

      precios: {
        precio_costo_base: null,
        margen_ganancia_sugerido: null,
        precio_venta_sugerido: null,
      },

      sucursalesSeleccionadas: [],
    };
  },

  computed: {
    ...mapGetters('marcas', ['marcas']),
    ...mapState('sucursales', ['sucursales']),

    step1Complete() {
      return this.producto.descripcion.trim().length > 0;
    },
    step2Complete() {
      return this.step1Complete && !!(this.producto.categoria && this.producto.categoria.id);
    },
    step3Complete() {
      return this.step2Complete;
    },
    nuevaPresentacionValida() {
      return (
        this.nuevaPresentacion.nombre.trim().length > 0 &&
        Number(this.nuevaPresentacion.factor_conversion) >= 2
      );
    },
  },

  methods: {
    ...mapActions('marcas', ['fetch_marcas']),
    ...mapActions('sucursales', ['fetch_sucursales']),

    setCategoria(cat) {
      this.producto.categoria = cat || { id: null };
    },

    buscarProveedores(search) {
      clearTimeout(this.debounceProveedores);
      this.debounceProveedores = setTimeout(async () => {
        this.loadingProveedores = true;
        try {
          const params = new URLSearchParams({ page: 1, page_size: 20 });
          if (search) params.append('search', search);
          const { data } = await this.axios.get(`/proveedores/?${params}`);
          this.proveedoresLocal = Array.isArray(data.results) ? data.results : [];
        } catch (e) {
          this.proveedoresLocal = [];
        } finally {
          this.loadingProveedores = false;
        }
      }, 300);
    },

    agregarPresentacion() {
      if (!this.nuevaPresentacionValida) return;
      this.presentaciones.push({
        nombre: this.nuevaPresentacion.nombre.trim(),
        factor_conversion: Number(this.nuevaPresentacion.factor_conversion),
        es_base: false,
      });
      this.cancelarNuevaPresentacion();
    },

    cancelarNuevaPresentacion() {
      this.nuevaPresentacion = { nombre: '', factor_conversion: null };
      this.mostrarFormPresentacion = false;
    },

    eliminarPresentacion(index) {
      this.presentaciones.splice(index, 1);
    },

    quitarSucursal(item) {
      this.sucursalesSeleccionadas = this.sucursalesSeleccionadas.filter(
        (s) => s.id !== item.id
      );
    },

    async registrar() {
      this.loading = true;
      try {
        // 1. Crear producto
        const { data } = await this.axios.post('/productos/productos', {
          descripcion: this.producto.descripcion,
          codigoDeBarra: this.producto.codigoDeBarra,
          marca: this.producto.marca,
          proveedor_id: this.producto.proveedor?.id || null,
          categoria: this.producto.categoria,
          es_elaboracion_propia: this.esElaboracionPropia,
          alicuota_iva: this.producto.alicuota_iva,
          precio_costo_base: this.precios.precio_costo_base,
          margen_ganancia_sugerido: this.precios.margen_ganancia_sugerido,
          precio_venta_sugerido: this.precios.precio_venta_sugerido,
        });

        const productoId = data.producto_empresa_id;

        // 2. Crear presentaciones adicionales (la base la crea el back automáticamente)
        const presentacionesAdicionales = this.presentaciones.filter((pp) => !pp.es_base);
        for (const pp of presentacionesAdicionales) {
          await this.axios.post(
            `/productos/productos/${productoId}/presentaciones`,
            {
              nombre: pp.nombre,
              factor_base: pp.factor_conversion,
              es_base: false,
              es_vendible: true,
              es_comprable: !this.esElaboracionPropia,
              activo: true,
            }
          );
        }

        // 3. Configurar PSP por sucursal (si se completó el paso 4)
        const tienePrecio = !!this.precios.precio_costo_base;
        const tieneSucursales = this.sucursalesSeleccionadas.length > 0;

        let preciosOk = true;
        if (tienePrecio && tieneSucursales) {
          try {
            // Obtener las presentaciones creadas para conocer sus IDs
            const { data: ppList } = await this.axios.get(
              `/productos/productos/${productoId}/presentaciones`
            );

            // Calcular ganancia real desde precio_venta / costo para que el backend
            // reconstruya el precio_venta correctamente (el endpoint ignora precio_venta).
            const costo = parseFloat(this.precios.precio_costo_base) || 0;
            const pv = parseFloat(this.precios.precio_venta_sugerido) || 0;
            const gananciaCalculada = (costo > 0 && pv > 0)
              ? parseFloat(((pv / costo) - 1) * 100)
              : (parseFloat(this.precios.margen_ganancia_sugerido) || 0);

            const preciosPayload = ppList.map((pp) => {
              const item = {
                pp_id: pp.id,
                ganancia: gananciaCalculada,
              };
              if (pp.es_base) item.costo = this.precios.precio_costo_base;
              return item;
            });

            for (const sucursal of this.sucursalesSeleccionadas) {
              await ProductosAPI.configurarPspSucursal({
                producto_id: productoId,
                sucursal_uuid: sucursal.uuid,
                precios: preciosPayload,
              });
            }
          } catch (e) {
            console.error('Error al configurar precios:', e);
            preciosOk = false;
          }
        }

        this.$updateBus.$emit('showSnackBar', {
          show: true,
          button: false,
          color: preciosOk ? 'success' : 'warning',
          text: preciosOk
            ? 'Producto registrado'
            : 'Producto registrado, pero hubo un error al configurar los precios',
          icon: preciosOk ? 'mdi-check' : 'mdi-alert',
          timeout: preciosOk ? 3000 : 6000,
        });

        this.$router.push({
          name: 'empresa.producto.detalle',
          params: { id_producto: productoId },
        });
      } catch (e) {
        console.error(e);
        const status = e.response?.status;
        const detail = e.response?.data?.detail || e.response?.data;
        const isUserError = status === 400 && typeof detail === 'string';
        this.$updateBus.$emit('showSnackBar', {
          show: true,
          button: false,
          color: isUserError ? 'warning' : 'error',
          text: isUserError ? detail : 'Error al registrar el producto',
          icon: isUserError ? 'mdi-alert-circle-outline' : 'mdi-alert',
          timeout: isUserError ? 5000 : 3000,
        });
      } finally {
        this.loading = false;
      }
    },
  },

  created() {
    this.fetch_marcas();
    this.buscarProveedores('');
    this.fetch_sucursales();
  },
};
</script>

<style scoped>
.step-indicator {
  display: flex;
  flex-direction: column;
  align-items: center;
  min-width: 36px;
  width: 36px;
  padding-top: 16px;
}

.step-circle {
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 13px;
  flex-shrink: 0;
  transition: background-color 0.3s ease, color 0.3s ease;
}

.step-circle--active {
  background-color: #1976d2;
  color: #fff;
}

.step-circle--done {
  background-color: #43a047;
  color: #fff;
}

.step-circle--pending {
  background-color: #e0e0e0;
  color: #9e9e9e;
}

.step-connector {
  width: 2px;
  flex-grow: 1;
  min-height: 20px;
  background-color: #e0e0e0;
  margin: 6px 0 0;
}

.step-disabled {
  opacity: 0.4;
  pointer-events: none;
}
</style>
